'use client';

import * as React from 'react';

type ScreenReaderPriority = 'polite' | 'assertive';

const announceToLiveRegion = (
  message: string,
  priority: ScreenReaderPriority = 'polite'
) => {
  if (typeof document === 'undefined') {
    return;
  }

  const elementId = priority === 'assertive' ? 'sr-live-assertive' : 'sr-live-polite';
  const element = document.getElementById(elementId);

  if (!element) {
    return;
  }

  element.textContent = '';
  setTimeout(() => {
    element.textContent = message;
  }, 10);
};

export const announceScreenReaderMessage = (
  message: string,
  priority: ScreenReaderPriority = 'polite'
) => {
  if (!message) {
    return;
  }

  announceToLiveRegion(message, priority);
};

/**
 * Global screen reader live region for announcements
 * Should be included once at the root level of the application
 */
export function ScreenReaderLive() {
  return (
    <>
      {/* Polite announcements for non-urgent updates */}
      <div 
        id="sr-live-polite" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      />
      
      {/* Assertive announcements for urgent updates */}
      <div 
        id="sr-live-assertive" 
        aria-live="assertive" 
        aria-atomic="true"
        className="sr-only"
      />
    </>
  );
}

/**
 * Hook to announce messages to screen readers
 */
export function useScreenReaderAnnounce() {
  const announce = React.useCallback((
    message: string, 
    priority: 'polite' | 'assertive' = 'polite'
  ) => {
    announceScreenReaderMessage(message, priority);
  }, []);

  return { announce };
}